import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { sbEditable } from '@storyblok/storyblok-editable';

import Slider, { SlideItem } from 'components/organisms/Slider';
import { Container, Row, Col } from 'components/atoms/Grid';
import Colors from 'components/tokens/Colors';
import Text from 'components/atoms/Text';
import { getAssetURL } from 'helpers/getAssetURL';
import { useDisplayedNews, useEditor } from 'providers/generalContext';
import RouterLink from 'components/atoms/RouterLink';
import getNewsUrl from 'helpers/getNewsUrl';
import { getHat } from 'helpers/getHat';
import Tag from 'components/molecules/Tag';
import placeholder from 'assets/image_placeholder.png';
import { IsMobile } from 'helpers/isMobile';

import { GetAllContent } from 'helpers/getAllContentStories';
import { useLazyQuery } from '@apollo/client';
import { gql } from 'apollo-boost';
import SplashScreen from 'components/organisms/SplashScreen';

function getImages(item, type) {
    if (type === 'horizontal') {
        if (item.image?.filename) {
            return item.image;
        } else if (item.article?.content?.featured_image?.filename) {
            return item.article?.content?.featured_image;
        } else if (item.vertical_image?.filename) {
            return item.vertical_image;
        } else if (item.article?.content?.featured_vertical_image?.filename) {
            return item.article?.content?.featured_vertical_image;
        }
        return placeholder;
    }

    if (type === 'vertical') {
        if (item.vertical_image?.filename) {
            return item.vertical_image;
        } else if (item.article?.content?.featured_vertical_image?.filename) {
            return item.article?.content?.featured_vertical_image;
        } else if (item.image?.filename) {
            return item.image;
        } else if (item.article?.content?.featured_image?.filename) {
            return item.article?.content?.featured_image;
        }
        return placeholder;
    }

    return placeholder;
}

function getAuthors(authors, authorsRelation) {
    if (authors && Array.isArray(authors)) {
        let arr = authors?.map(author => {
            return { name: authorsRelation[author] };
        });
        return arr;
    }
    return [{ name: 'Equipe Lupa' }];
}

const query = gql`
    query ($uuids: String) {
        CollaboratorItems(by_uuids: $uuids) {
            items {
                uuid
                name
            }
        }
    }
`;

function MainNews(props) {
    const { isStoryblokEditor } = useEditor();
    const mobile = IsMobile();
    const { setDisplayedNews } = useDisplayedNews();

    const quantity = Number(props.articles_quantity);
    const tag = props.tag || 'home-destaque';

    const imagePosition = props.image_position || 'flex-end';
    const titlePosition = props.title_position || 'flex-start';

    const [customArticleAuthors, setCustomArticleAuthors] = useState({});
    const allAuthors = props.custom_articles
        ?.flatMap(item => item.article?.content?.authors)
        ?.filter(value => value !== undefined);

    const [getCollaborators] = useLazyQuery(query, {
        variables: { uuids: allAuthors?.toString() },
        onCompleted: data => {
            let obj = {};
            data.CollaboratorItems.items.forEach(author => {
                obj[author.uuid] = author.name;
            });
            setCustomArticleAuthors(oldState => ({ ...oldState, ...obj }));
        }
    });

    const [news, setNews] = useState([]);

    const { getQuery, loading, data } = GetAllContent(tag, quantity);

    const newsId = Array.from(
        new Set([...data?.map(item => item.id), ...props.custom_articles?.map(item => item.article.id)])
    );

    useEffect(() => {
        if (quantity > 0 && !data.length) {
            getQuery();
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!loading && data.length) {
            setNews(oldState => {
                if (oldState?.length) {
                    let arr = [...oldState, ...data];
                    let noDuplicates = arr?.filter((v, i, a) => a?.findIndex(v2 => v2.id === v.id) === i);

                    return noDuplicates;
                }
                return data;
            });

            setDisplayedNews(oldState => ({
                ...oldState,
                [props.index]: { newsId: newsId, ...oldState[props.index] }
            }));
        }
    }, [loading]);

    useEffect(() => {
        if (props.custom_articles.length && !Object.entries(customArticleAuthors).length) {
            getCollaborators();
        }

        if (props.custom_articles.length && Object.entries(customArticleAuthors).length) {
            setNews(oldState => {
                let articles = props.custom_articles.map(article => {
                    if (
                        (Array.isArray(article?.article?.content?.authors) &&
                            article?.article?.content?.authors?.some(uuid => customArticleAuthors[uuid])) ||
                        article?.article?.content?.component === 'article'
                    ) {
                        let obj = {
                            ...article.article,
                            content: {
                                ...article.article.content,
                                featured_title:
                                    article.title || article.article?.content?.featured_title || article.article?.name,
                                featured_vertical_image: getImages(article, 'vertical'),
                                featured_image: getImages(article, 'horizontal'),
                                authors: getAuthors(article.article.content.authors, customArticleAuthors)
                            }
                        };

                        return obj;
                    } else {
                        return article;
                    }
                });
                return [...oldState, ...articles];
            });
        }
    }, [customArticleAuthors]);

    if (!data?.length) return <SplashScreen />;

    return (
        <Container fluid {...sbEditable(props)}>
            <Row>
                <Col size="12" px="0">
                    <Slider>
                        {news?.map(item => (
                            <SlideItem key={item.uuid} bg={Colors.black}>
                                <ContentBanner>
                                    <Container px="0">
                                        <Row style={{ minHeight: `640px`, position: 'relative' }} alignItems="center">
                                            <ImageBox>
                                                <Row justifyContent={imagePosition} mt="5 10" mb="5 10">
                                                    <Col size="12 8" px="0 6">
                                                        <Img
                                                            src={
                                                                getAssetURL(
                                                                    mobile
                                                                        ? item.content.featured_vertical_image
                                                                              .filename ||
                                                                              item.content.featured_image.filename
                                                                        : item.content.featured_image.filename ||
                                                                              item.content.featured_vertical_image
                                                                                  .filename
                                                                ) || placeholder
                                                            }
                                                            alt={item.content?.featured_image?.alt}
                                                        />
                                                    </Col>
                                                </Row>
                                            </ImageBox>
                                            <TitleWrapper position={titlePosition}>
                                                <Col size="12 6" mt="20 0" style={{ marginLeft: !mobile && '8.33%' }}>
                                                    <Row>
                                                        <Col size="12" px="1">
                                                            <RouterLink
                                                                to={getNewsUrl(item.full_slug, item.first_published_at)}
                                                                editor={isStoryblokEditor}
                                                                decoration="none"
                                                                weight="normal"
                                                            >
                                                                <Tag primary className="tag">
                                                                    {getHat(
                                                                        item?.content?.hat,
                                                                        item?.content?.custom_hat
                                                                    )}
                                                                </Tag>
                                                            </RouterLink>
                                                        </Col>
                                                        <Col size="12" mb="3">
                                                            <RouterLink
                                                                to={getNewsUrl(item.full_slug, item.first_published_at)}
                                                                editor={isStoryblokEditor}
                                                                decoration="none"
                                                            >
                                                                <Text
                                                                    color={Colors.black}
                                                                    bg={Colors.white}
                                                                    title
                                                                    highlight
                                                                    size="48 56"
                                                                    lineHeight="55 64"
                                                                    style={{ paddingBottom: 0 }}
                                                                >
                                                                    {item?.content?.featured_title || item?.name}
                                                                </Text>
                                                            </RouterLink>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </TitleWrapper>
                                        </Row>
                                        <Row alignItems="center">
                                            <Col size="hidden 8" mb="6" />
                                            <Col size="12 4" mb="13 6" text="left right" pl="3.5 1" pr="3 3 5">
                                                <RouterLink
                                                    to={getNewsUrl(item.full_slug, item.first_published_at)}
                                                    editor={isStoryblokEditor}
                                                    color={Colors.white}
                                                    fontSize="16"
                                                >
                                                    Saiba mais
                                                </RouterLink>
                                            </Col>
                                        </Row>
                                    </Container>
                                </ContentBanner>
                            </SlideItem>
                        ))}
                    </Slider>
                </Col>
            </Row>
        </Container>
    );
}

const StyledRow = styled(Row)`
    position: relative;
    min-height: 480px;

    @media (min-width: 768px) {
        min-height: 640px;
    }
`;

const ContentBanner = styled.div`
    float: left;
    width: 100%;
    color: ${Colors.white};

    .tag {
        padding-bottom: 0.5rem;
    }

    @media (min-width: 1600px) {
        .tag {
            padding-bottom: 0.5rem;
        }
    }
`;

const ImageBox = styled.div`
    float: left;
    position: absolute;
    height: 100%;
    width: 100%;

    img {
        width: 100%;
        height: 44.2rem;

        @media (max-width: 680px) {
            height: 37.6rem;
        }
    }
`;

const Img = styled.div`
    width: 100%;
    height: 44.2rem;
    background-image: url(${({ src }) => src});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    @media (max-width: 680px) {
        height: 37.6rem;
    }
`;

const TitleWrapper = styled.div`
    display: flex;
    justify-content: ${({ position }) => (position ? position : 'flex-end')};
    width: 100vw;
    padding-left: 3rem;
`;

export default MainNews;
